import { memo, useRef, useState } from 'react';
import { Tabs } from 'radix-ui';
import { useGeneratorsByLevel } from '../../store/global-context';
import { UnitType } from '../../types/game-types';
import { GeneratorsRow } from '../GeneratorsRow';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { InspirationMeter } from '../InspirationMeter';
import { ManaMeter } from '../ManaMeter';
import { DungeonKeysButton } from '../DungeonKeysButton';
import { UpgradesTreeMenu } from '../../components/UpgradesTreeMenu';
import { useHotkeys } from 'react-hotkeys-hook';
import { HotkeyHint } from '@game-libs/exc-libs';
import { AudioManager } from '@game-libs/audio-manager';
import { MenuEvents } from '@game-libs/models';

export const GeneratorsList = memo(function GeneratorsList() {
  const { playSFXNow } = AudioManager();
  const { t } = useTranslation('generators');
  const level1Generators = useGeneratorsByLevel(1, UnitType.TROOPS);
  const level2Generators = useGeneratorsByLevel(2, UnitType.TROOPS);
  const level3Generators = useGeneratorsByLevel(3, UnitType.TROOPS);
  const [selectedType, setSelectedType] = useState('troops');

  useHotkeys(
    'q',
    () => {
      setSelectedType('troops');
    },
    [],
  );

  useHotkeys(
    'w',
    () => {
      setSelectedType('support');
    },
    [],
  );

  // const isAnyPurchasedRef = useRef(false);
  const isAnyUnlockedRef = useRef(false);
  const isLevel2UnlockedRef = useRef(false);
  const isLevel3UnlockedRef = useRef(false);

  // if (!isAnyPurchasedRef.current) {
  //   isAnyPurchasedRef.current = level1Generators.some((g) => g.purchased > 0);
  // }

  if (!isAnyUnlockedRef.current) {
    isAnyUnlockedRef.current = level1Generators.some((g) => g.isUnlocked);
  }

  if (!isLevel2UnlockedRef.current) {
    isLevel2UnlockedRef.current = level2Generators.some((g) => g.isUnlocked);
  }

  if (!isLevel3UnlockedRef.current) {
    isLevel3UnlockedRef.current = level3Generators.some((g) => g.isUnlocked);
  }

  if (!isAnyUnlockedRef.current) return null;

  return (
    <Tabs.Root
      className={styles.tabsRoot}
      defaultValue={'troops'}
      orientation="vertical"
      value={selectedType}
      onValueChange={(value) => {
        setSelectedType(value);
        playSFXNow(MenuEvents.CHANGE_VALUE);
      }}
    >
      <Tabs.List className={styles.tabsButtons} aria-label="upgrades tabs">
        <Tabs.Trigger
          key={'troops'}
          className={`radix-tabs-trigger ${styles.trigger}`}
          value={'troops'}
          data-testid="generators-list-troops-tab"
        >
          {t(`tabs.troops`)}
          <HotkeyHint hint={'q'} />
        </Tabs.Trigger>
        <Tabs.Trigger
          key={'support'}
          className={`radix-tabs-trigger ${styles.trigger}`}
          value={'support'}
          data-testid="generators-list-support-tab"
        >
          {t(`tabs.support`)}
          <HotkeyHint hint={'w'} />
        </Tabs.Trigger>
        <div className={`generatorsHeader ${styles.generatorsHeader}`}>
          <UpgradesTreeMenu />
          <div className={styles.metersContainer}>
            <ManaMeter />
            <div className={styles.inspirationMeter}>
              <InspirationMeter />
            </div>
            <DungeonKeysButton />
          </div>
        </div>
      </Tabs.List>
      <div className={styles.upgradesFrame} id="generators-list-container">
        <Tabs.Content key={'troops'} value={'troops'}>
          <div className={styles.generatorsList}>
            <GeneratorsRow level={1} type={UnitType.TROOPS} />
            {isLevel2UnlockedRef.current && <GeneratorsRow level={2} type={UnitType.TROOPS} />}
            {isLevel3UnlockedRef.current && <GeneratorsRow level={3} type={UnitType.TROOPS} />}
          </div>
        </Tabs.Content>
        <Tabs.Content key={'support'} value={'support'}>
          <div className={styles.generatorsList}>
            <GeneratorsRow level={1} type={UnitType.SUPPORT} />
            {isLevel2UnlockedRef.current && <GeneratorsRow level={2} type={UnitType.SUPPORT} />}
            {isLevel3UnlockedRef.current && <GeneratorsRow level={3} type={UnitType.SUPPORT} />}
          </div>
        </Tabs.Content>
      </div>
    </Tabs.Root>
  );
});
